import React, { memo, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
import Cookies from 'universal-cookie'
import {
  FaArrowLeft,
  FaArrowRight,
  FaArrowUp,
  FaArrowDown,
  FaHome
} from 'react-icons/fa'
import { MdAddBox, MdOutlineRemoveCircleOutline, MdClose } from 'react-icons/md'
import { IoMdRefresh } from 'react-icons/io'
import { RiArrowGoBackLine } from 'react-icons/ri'
import { getDeviceFriendlyName } from '../../../utils/functions'
import { sendViscaCommand, getDeviceIP } from '../../../utils/visca'
import { NotificationContext } from '../../../context/Shared/Notification.context'
import styles from './KioskFeedPreview.module.scss'

// Enumerate every connected camera, with its friendly name and configured VISCA IP
const enumerateFeeds = async () => {
  const cookies = new Cookies()

  // Prompt for permission so labels are populated
  await navigator.mediaDevices?.getUserMedia({ video: true }).catch(() => {})

  const devices = (await navigator.mediaDevices?.enumerateDevices()) || []

  const disabledRaw = cookies.get('disabledVideoDevices')
  let disabled = []
  if (disabledRaw) {
    try {
      disabled = typeof disabledRaw === 'string' ? JSON.parse(disabledRaw) : disabledRaw
    } catch (error) {
      disabled = []
    }
  }

  const feeds = []
  const seenGroupIds = new Set()
  devices.forEach((device, i) => {
    if (device.kind !== 'videoinput') return
    if (device.deviceId === 'default' || device.deviceId === 'communications') return
    if (device.groupId && seenGroupIds.has(device.groupId)) return
    if (device.groupId) seenGroupIds.add(device.groupId)
    if (disabled.includes(device.deviceId)) return

    const originalName = device.label || 'Camera ' + (i + 1)
    feeds.push({
      deviceId: device.deviceId,
      name: getDeviceFriendlyName(device.deviceId, originalName, cookies),
      ip: getDeviceIP(device.deviceId) || ''
    })
  })

  return feeds
}

// A single camera tile: live preview + (when an IP is configured) PTZ test controls
const FeedTile = memo(({ feed }) => {
  const { t } = useTranslation()
  const videoRef = useRef(null)
  const [error, setError] = useState(false)

  useEffect(() => {
    let stream
    let cancelled = false

    const start = async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { deviceId: { exact: feed.deviceId } }
        })
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop())
          return
        }
        if (videoRef.current) {
          videoRef.current.srcObject = stream
        }
      } catch (err) {
        if (!cancelled) setError(true)
      }
    }

    start()

    return () => {
      cancelled = true
      if (stream) {
        stream.getTracks().forEach((track) => track.stop())
      }
    }
  }, [feed.deviceId])

  // Fire a VISCA command for this camera; no-op (with a hint) if no IP is set
  const send = useCallback(
    (command) => () => {
      if (!feed.ip) return
      sendViscaCommand(feed.ip, command)
    },
    [feed.ip]
  )

  // Press-and-hold semantics: move on press, stop shortly after release
  const holdStop = (stopCommand) => () => {
    if (!feed.ip) return
    setTimeout(() => sendViscaCommand(feed.ip, stopCommand), 75)
  }

  const ptzButton = (icon, command, stopCommand) => (
    <button
      type="button"
      className={styles.feedTile__ptzButton}
      onMouseDown={send(command)}
      onMouseUp={holdStop(stopCommand)}
      onMouseLeave={holdStop(stopCommand)}
      onTouchStart={(e) => {
        e.preventDefault()
        send(command)()
      }}
      onTouchEnd={(e) => {
        e.preventDefault()
        holdStop(stopCommand)()
      }}
      onTouchCancel={(e) => {
        e.preventDefault()
        holdStop(stopCommand)()
      }}
    >
      {icon}
    </button>
  )

  return (
    <div className={styles.feedTile}>
      <div className={styles.feedTile__videoWrapper}>
        {error
          ? (
          <div className={styles.feedTile__error}>{t('Unable to open feed')}</div>
            )
          : (
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className={styles.feedTile__video}
          />
            )}
        {feed.ip
          ? (
          <div className={styles.feedTile__ptz}>
            <div className={styles.feedTile__ptzRow}>
              {ptzButton(<FaArrowUp />, 'go up', 'go stop')}
            </div>
            <div className={styles.feedTile__ptzRow}>
              {ptzButton(<FaArrowLeft />, 'go left', 'go stop')}
              {ptzButton(<FaHome />, 'go home', 'go stop')}
              {ptzButton(<FaArrowRight />, 'go right', 'go stop')}
            </div>
            <div className={styles.feedTile__ptzRow}>
              {ptzButton(<FaArrowDown />, 'go down', 'go stop')}
            </div>
            <div className={styles.feedTile__ptzRow}>
              {ptzButton(<MdAddBox />, 'zoom in', 'zoom stop')}
              <button
                type="button"
                className={styles.feedTile__ptzButton}
                onClick={send('zoom reset')}
              >
                <RiArrowGoBackLine />
              </button>
              {ptzButton(<MdOutlineRemoveCircleOutline />, 'zoom out', 'zoom stop')}
            </div>
          </div>
            )
          : null}
      </div>
      <div className={styles.feedTile__footer}>
        <span className={styles.feedTile__name}>{feed.name}</span>
        <span
          className={`${styles.feedTile__badge} ${
            feed.ip ? styles['feedTile__badge--ptz'] : styles['feedTile__badge--noPtz']
          }`}
        >
          {feed.ip ? t('PTZ ready') : t('No PTZ (set IP in Settings)')}
        </span>
      </div>
    </div>
  )
})

FeedTile.displayName = 'FeedTile'

FeedTile.propTypes = {
  feed: PropTypes.shape({
    deviceId: PropTypes.string,
    name: PropTypes.string,
    ip: PropTypes.string
  }).isRequired
}

const KioskFeedPreview = ({ onClose }) => {
  const { t } = useTranslation()
  const { setSnackBarMessage } = useContext(NotificationContext)
  const [feeds, setFeeds] = useState([])
  const [loading, setLoading] = useState(true)

  const loadFeeds = useCallback(async () => {
    setLoading(true)
    try {
      const found = await enumerateFeeds()
      setFeeds(found)
    } catch (err) {
      setSnackBarMessage(t('Unable to list video feeds'), 'error')
    } finally {
      setLoading(false)
    }
  }, [setSnackBarMessage, t])

  useEffect(() => {
    loadFeeds()
  }, [loadFeeds])

  return (
    <div className={styles.preview}>
      <div className={styles.preview__header}>
        <h2 className={styles.preview__title}>{t('Test Video Feeds')}</h2>
        <div className={styles.preview__actions}>
          <button
            type="button"
            className={styles.preview__iconButton}
            onClick={loadFeeds}
            aria-label={t('Refresh')}
          >
            <IoMdRefresh />
          </button>
          <button
            type="button"
            className={styles.preview__iconButton}
            onClick={onClose}
            aria-label={t('Close')}
          >
            <MdClose />
          </button>
        </div>
      </div>

      {loading
        ? (
        <div className={styles.preview__empty}>{t('Loading feeds...')}</div>
          )
        : feeds.length === 0
          ? (
        <div className={styles.preview__empty}>{t('No video feeds found')}</div>
            )
          : (
        <div className={styles.preview__grid}>
          {feeds.map((feed) => (
            <FeedTile key={feed.deviceId} feed={feed} />
          ))}
        </div>
            )}
    </div>
  )
}

KioskFeedPreview.propTypes = {
  onClose: PropTypes.func.isRequired
}

export default memo(KioskFeedPreview)
